import { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Shield, CreditCard, Smartphone, CheckCircle, XCircle, Clock, AlertCircle, ExternalLink, Wallet } from 'lucide-react';
import api from '../services/api';

const fmt = (n) =>
  new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', minimumFractionDigits: 0 }).format(n ?? 0);

function normalisePhone(raw) {
  let p = String(raw).replace(/\D/g, '');
  if (p.startsWith('0'))    p = '256' + p.slice(1);
  if (!p.startsWith('256')) p = '256' + p;
  return p;
}

export default function PublicChargePage() {
  const { chargeId } = useParams();
  const navigate = useNavigate();

  const [charge, setCharge]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [step, setStep]           = useState('select'); // select | wallet | momo | polling | done | failed
  const [phone, setPhone]         = useState('');
  const [paying, setPaying]       = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [momoError, setMomoError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/charge/${chargeId}`);
      const c = r.charge;
      setCharge(c);
      if (c.status === 'succeeded')       setStep('done');
      else if (c.status === 'failed')      setStep('failed');
      else if (c.status === 'expired')     setError('This payment link has expired.');
      else if (c.hasPendingMomo)           setStep('polling');
    } catch {
      setError('Charge not found or expired.');
    } finally {
      setLoading(false);
    }
  }, [chargeId]);

  useEffect(() => { load(); }, [load]);

  // Poll for momo status
  useEffect(() => {
    if (step !== 'polling') return;
    const interval = setInterval(async () => {
      try {
        const r = await api.get(`/charge/${chargeId}`);
        const c = r.charge;
        setCharge(c);
        setPollCount(n => n + 1);
        if (c.status === 'succeeded') { clearInterval(interval); setStep('done'); }
        if (c.status === 'failed')    { clearInterval(interval); setStep('failed'); }
        if (c.status === 'expired')   { clearInterval(interval); setError('Payment expired.'); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [step, chargeId]);

  const payWallet = async () => {
    const token = localStorage.getItem('dwi_token');
    if (!token) {
      localStorage.setItem('charge_return', `/pay/c/${chargeId}`);
      navigate('/login');
      return;
    }
    setPaying(true);
    try {
      await api.post(`/charge/${chargeId}/pay/wallet`, {});
      setStep('done');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally { setPaying(false); }
  };

  const payMomo = async () => {
    if (!phone.trim()) { toast.error('Enter your phone number'); return; }
    setMomoError(null);
    setPaying(true);
    try {
      const r = await api.post(`/charge/${chargeId}/pay/momo`, { phone: normalisePhone(phone) });
      const data = r.data || r;
      if (data.succeeded) {
        // Sync SUCCEEDED path: Yo! confirmed inline — reload charge and show done
        await load();
      } else {
        // alreadyProcessing or normal async path: go to polling
        setStep('polling');
      }
    } catch (err) {
      // 404 = charge already paid (succeeded) — reload and show done screen
      if (err.response?.status === 404) {
        const reload = await api.get(`/charge/${chargeId}`).catch(() => null);
        const c = reload?.data?.charge;
        if (c?.status === 'succeeded') { setCharge(c); setStep('done'); setPaying(false); return; }
      }
      const msg = err.response?.data?.message
        || err.response?.data?.error
        || 'Payment initiation failed — please try again.';
      setMomoError(msg);
    } finally { setPaying(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-sm w-full text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-white font-semibold mb-2">Payment Unavailable</p>
        <p className="text-slate-400 text-sm">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4">

        {/* Header */}
        <div className="text-center">
          {charge?.merchant?.logoUrl
            ? <img src={charge.merchant.logoUrl} alt={charge.merchant.businessName} className="h-12 mx-auto mb-3 rounded-xl object-contain" />
            : <div className="w-12 h-12 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto mb-3">
                <Wallet className="w-6 h-6 text-cyan-400" />
              </div>}
          <h2 className="text-white font-bold text-lg">{charge?.merchant?.businessName || 'Merchant'}</h2>
          {charge?.mode === 'test' && (
            <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
              TEST MODE — no real payment
            </span>
          )}
        </div>

        {/* Amount card */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 text-center">
          <p className="text-slate-400 text-sm mb-1">{charge?.description || 'Payment'}</p>
          <p className="text-3xl font-black text-white">{fmt(charge?.amount)}</p>
          {charge?.expiresAt && step === 'select' && (
            <p className="text-slate-500 text-xs mt-2 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" />
              Expires {new Date(charge.expiresAt).toLocaleTimeString('en-UG', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {/* Step: select payment method */}
        {step === 'select' && (
          <div className="space-y-3">
            <p className="text-slate-400 text-xs text-center font-medium uppercase tracking-wide">Choose payment method</p>
            <button
              onClick={() => setStep('wallet')}
              className="w-full bg-cyan-500/10 border border-cyan-500/30 hover:border-cyan-400 rounded-xl p-4 flex items-center gap-3 text-left transition-all"
            >
              <CreditCard className="w-5 h-5 text-cyan-400 shrink-0" />
              <div>
                <p className="text-white font-semibold text-sm">Pay with DWIPay Wallet</p>
                <p className="text-slate-400 text-xs">Instant — login required</p>
              </div>
            </button>
            <button
              onClick={() => setStep('momo')}
              className="w-full bg-slate-800 border border-slate-700 hover:border-slate-500 rounded-xl p-4 flex items-center gap-3 text-left transition-all"
            >
              <Smartphone className="w-5 h-5 text-green-400 shrink-0" />
              <div>
                <p className="text-white font-semibold text-sm">Pay with Mobile Money</p>
                <p className="text-slate-400 text-xs">MTN or Airtel — no account needed</p>
              </div>
            </button>
            {/* Dynamic charge QR — payer can scan to share / open on another device */}
            <div className="flex flex-col items-center gap-2 pt-2">
              <p className="text-slate-500 text-xs uppercase tracking-wider font-medium">Or share this QR</p>
              <div className="bg-white p-2 rounded-xl inline-block">
                <QRCodeSVG value={window.location.href} size={96} />
              </div>
            </div>
          </div>
        )}

        {/* Step: wallet payment */}
        {step === 'wallet' && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 space-y-4">
            <p className="text-white font-semibold text-sm text-center">Pay with DWIPay Wallet</p>
            <p className="text-slate-400 text-xs text-center">You'll be logged in to authorise the payment.</p>
            <div className="space-y-2">
              <button onClick={payWallet} disabled={paying}
                className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-sm transition-all disabled:opacity-50">
                {paying ? 'Processing...' : `Pay ${fmt(charge?.amount)}`}
              </button>
              <button onClick={() => setStep('select')}
                className="w-full py-2 rounded-xl text-slate-400 hover:text-white text-sm">
                ← Back
              </button>
            </div>
          </div>
        )}

        {/* Step: momo */}
        {step === 'momo' && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 space-y-4">
            <p className="text-white font-semibold text-sm text-center">Mobile Money Payment</p>
            <div>
              <label className="text-slate-400 text-xs font-medium block mb-1.5">Phone Number</label>
              <input
                type="tel"
                placeholder="07X XXX XXXX or 256..."
                value={phone}
                onChange={e => { setPhone(e.target.value); setMomoError(null); }}
                className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-cyan-500"
              />
              <p className="text-slate-500 text-xs mt-1">MTN or Airtel Uganda</p>
            </div>

            {/* Inline error with retry cue — shown instead of spinner on failure */}
            {momoError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm text-red-300 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{momoError}</span>
              </div>
            )}

            <div className="space-y-2">
              <button onClick={payMomo} disabled={paying}
                className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold text-sm transition-all disabled:opacity-50">
                {paying ? 'Sending prompt...' : momoError ? 'Try again →' : `Pay ${fmt(charge?.amount)}`}
              </button>
              <button onClick={() => { setStep('select'); setMomoError(null); }}
                className="w-full py-2 rounded-xl text-slate-400 hover:text-white text-sm">
                ← Back
              </button>
            </div>
          </div>
        )}

        {/* Step: polling */}
        {step === 'polling' && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 text-center space-y-4">
            <div className="w-12 h-12 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
            <div>
              <p className="text-white font-semibold">Check your phone</p>
              <p className="text-slate-400 text-sm mt-1">Approve the mobile money prompt on your phone</p>
              {pollCount > 10
                ? <p className="text-amber-400 text-xs mt-2">
                    If you already entered your PIN, your payment is being confirmed — do not pay again.
                  </p>
                : <p className="text-slate-500 text-xs mt-2">Waiting for confirmation… ({pollCount * 3}s)</p>
              }
            </div>
          </div>
        )}

        {/* Step: done */}
        {step === 'done' && (
          <div className="bg-slate-800 border border-emerald-500/30 rounded-2xl p-6 text-center space-y-4">
            <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto" />
            <div>
              <p className="text-white font-bold text-lg">Payment Successful</p>
              <p className="text-slate-400 text-sm mt-1">{fmt(charge?.amount)} paid to {charge?.merchant?.businessName}</p>
            </div>
            {charge?.redirectUrl
              ? <a href={charge.redirectUrl}
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm">
                  Return to merchant <ExternalLink className="w-3 h-3" />
                </a>
              : null}
            <div className="bg-slate-700/50 rounded-xl p-3 text-xs text-slate-400 text-left space-y-1">
              <p>Don't have a DWIPay Wallet?</p>
              <a href="/register" className="text-cyan-400 hover:underline font-medium">
                Create one free — track payments, earn rewards →
              </a>
            </div>
          </div>
        )}

        {/* Step: failed */}
        {step === 'failed' && (
          <div className="bg-slate-800 border border-red-500/30 rounded-2xl p-6 text-center space-y-4">
            <XCircle className="w-14 h-14 text-red-400 mx-auto" />
            <div>
              <p className="text-white font-bold text-lg">Payment Failed</p>
              <p className="text-slate-400 text-sm mt-1">The payment could not be completed.</p>
            </div>
            <button onClick={() => { setStep('select'); load(); }}
              className="w-full py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium">
              Try Again
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center">
          <p className="text-slate-600 text-xs flex items-center justify-center gap-1">
            <Shield className="w-3 h-3" /> Secured by DWI Fintech OS
          </p>
        </div>
      </div>
    </div>
  );
}
